import { UserOutlined } from "@ant-design/icons"
import { Divider, Tag, Tooltip } from "antd"
import dayjs from 'dayjs'
import { getFormattedDate } from "../../utils/getLocalFormattedDate"
import { useTenantNavigation } from "../../hooks/useTenantNavigation"
import { useTranslation } from "react-i18next"
import { getStatusDisplayLabel } from "../../utils/workflowHelper"
import { useSelector } from "react-redux"
import { getCurrentSpaceData } from "../../utils/currentUserDetail"

const statusColorMapping = {
    "active": "blue",
    "approved": "green",
    "pending": "orange",
    "refused": "red",
    "submitted": "blue",
    "deleted": "red",
    "draft": "purple",
    "New": "purple"
}

const RequestCard = (props) => {
    const { spaceName, createdBy, dueDate, spaceId, status } = props
    const { userInfo, userGroupSpaces } = useSelector(state => state.user)
    const navigate = useTenantNavigation();
    const { t, i18n } = useTranslation();
    // The approvers payload carries no workflow, so the widget hands one in.
    const workflow = props.originalWorkflow || props.workflow
    const statusLabel = getStatusDisplayLabel(status, workflow, { language: i18n.language, t })
    let curDate = new Date(dayjs().format("YYYY/MM/DD"));
    let date2 = dueDate ? new Date(dueDate) : curDate;
    let dif = curDate - date2;
    let dayDiff = Math.round(dif / (1000 * 60 * 60 * 24));
    let dueText = "";
    if (dueDate) {
        if (dayDiff > 0) {
            dueText = dayDiff === 1 ? t("Overdue by 1 day") : t("Overdue by {{count}} days", { count: dayDiff });
        } else if (dayDiff === 0) {
            dueText = t("Due today");
        } else {
            const daysLeft = Math.abs(dayDiff);
            dueText = daysLeft === 1 ? t("Due in 1 day") : t("Due in {{count}} days", { count: daysLeft });
        }
    }
    // The user's spaces in Redux carry their translations; this resolves the
    // name / description in the current language.
    const currentSpace = getCurrentSpaceData(spaceId, userInfo?.spaceInfo, userGroupSpaces) || {}
    const spaceLabel = currentSpace.name || spaceName
    let formNode = (workflow?.nodes || workflow?.node || []).find(node => node?.data?.name === status)
    let reqDescription = ""
    if (formNode) {
        let reqFormData = props.workflowForms?.find(form => form.formId === formNode.formData?.formId)
        reqDescription = reqFormData ? reqFormData.formHeader?.description : ""
    } if (!reqDescription) {
        reqDescription = currentSpace?.description || ""
    }

    const navigateToRequest = () => {
        navigate(`/app/space/${spaceId}/approve/${props.workflowId}/${props.requestId}`)
    }; return (
        <div className="w-full bg-white border border-gray-200 rounded-xl p-3 sm:p-4 cursor-pointer transition-colors duration-200 shadow-none hover:border-blue-400 hover:shadow-none min-h-[160px] sm:min-h-[180px] md:min-h-[190px] lg:min-h-[200px] flex flex-col justify-between" onClick={navigateToRequest}>
            {/* Header with space name and status */}
            <div className="flex justify-between items-start mb-2 sm:mb-3 gap-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 flex-1">
                    <div className="bg-blue-50 text-blue-600 px-3 py-2 rounded-lg text-sm font-medium max-w-full sm:max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap text-center sm:text-left">
                        <Tooltip title={spaceLabel} placement="right">{spaceLabel}</Tooltip>
                    </div>
                    <Tag
                        color={statusColorMapping[status] || "orange"}
                        style={{ textTransform: "capitalize", fontSize: '14px', minHeight: '32px' }}
                        bordered={false}
                        size="default"
                        className="self-start sm:self-center px-3 py-1 text-sm font-medium max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                        {statusLabel}
                    </Tag>
                </div>
            </div>

            { /* Description */}
            <div className="text-xs sm:text-sm font-medium text-gray-800 mb-3 sm:mb-4 flex-1 leading-relaxed text-left">
                <div className="line-clamp-3 sm:line-clamp-2">
                    {reqDescription ? reqDescription : t("No description provided for this space")}
                </div>
            </div>

            <Divider className="my-2 border-gray-200" />

            {/* Footer with user and date */}
            <div className="flex  justify-between items-center gap-3 mt-auto">
                <Tooltip title={createdBy.email}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <UserOutlined className="text-gray-500 text-xs sm:text-sm flex-shrink-0" />
                        <div className="text-xs text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap text-left">
                            {createdBy.name}
                        </div>
                    </div>
                </Tooltip>

                {dueDate && (
                    <Tooltip title={getFormattedDate(dueDate)}>
                        <div className={`flex items-center gap-2 ${dif > 0 ? "text-red-500" : "text-gray-500"} flex-shrink-0`}>
                            <span className={`inline-block w-2 h-2 rounded-full ${dif >= 0 ? "bg-red-500" : "bg-gray-400"}`} />
                            <div className="text-xs font-normal whitespace-nowrap text-right">
                                {dueText}
                            </div>
                        </div>
                    </Tooltip>
                )}
            </div>
        </div>
    )
}

export default RequestCard
